import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Shield, Eye, EyeOff, RefreshCw, UserPlus, LogIn, Gift } from "lucide-react";
import { APP_VERSION } from "../version";

const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [formData, setFormData] = useState({
    login: "",
    password: "",
    captchaAnswer: ""
  });
  const [registerData, setRegisterData] = useState({
    login: "",
    email: "",
    password: "",
    confirmPassword: "",
    giftCode: ""
  });
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.is_admin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const fetchCaptcha = async () => {
    try {
      const response = await axios.get(`${API}/captcha`);
      setCaptcha(response.data);
    } catch (error) {
      setCaptcha({ num1: Math.floor(Math.random() * 9) + 1, num2: Math.floor(Math.random() * 9) + 1 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    const trimmedLogin = formData.login.trim();
    if (trimmedLogin.length < 1 || trimmedLogin.length > 100) {
      toast.error("Login invalide");
      return;
    }
    if (formData.password.length < 1 || formData.password.length > 200) {
      toast.error("Mot de passe invalide");
      return;
    }
    const answer = parseInt(formData.captchaAnswer);
    if (isNaN(answer)) {
      toast.error("Veuillez entrer la réponse du captcha");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        login: trimmedLogin,
        password: formData.password,
        captcha_answer: answer,
        captcha_token: captcha.captcha_token || ""
      });

      login(response.data.token, response.data.user);
      toast.success("Connexion réussie");
      
      if (response.data.user.is_admin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur de connexion";
      toast.error(message);
      fetchCaptcha();
      setFormData(prev => ({ ...prev, captchaAnswer: "" }));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (!registerData.giftCode.trim()) {
      toast.error("Le code gift est requis");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        login: registerData.login,
        email: registerData.email,
        password: registerData.password,
        confirm_password: registerData.confirmPassword,
        gift_code: registerData.giftCode
      });
      login(response.data.token, response.data.user);
      toast.success("Inscription réussie !");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, transparent 0%, hsl(var(--background)) 100%), url('https://images.unsplash.com/photo-1759159347827-de3a54002de7?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="text-5xl font-bold tracking-tight mb-4">
            <span className="text-primary">Panel</span>helper
          </h1>
          <p className="text-gray-300 text-lg max-w-md">
            Accédez facilement à tous vos outils Xtream Player en un clic. Profitez de fonctionnalités pratiques, telles que le téléchargement de codes, la gestion simplifiée et bien plus encore. Simplifiez votre expérience et optimisez votre utilisation dès maintenant !
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-sm border border-primary/20">
                {mode === "login" ? <Shield className="h-8 w-8 text-primary" /> : <UserPlus className="h-8 w-8 text-primary" />}
              </div>
            </div>
            {/* Toggle Login / Register */}
            <div className="flex bg-muted/50 rounded-sm p-1 gap-1">
              <button
                onClick={() => setMode("login")}
                className={`flex-1 py-2 px-3 rounded-sm text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                  mode === "login" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="tab-login"
              >
                <LogIn className="h-4 w-4" /> Connexion
              </button>
              <button
                onClick={() => setMode("register")}
                className={`flex-1 py-2 px-3 rounded-sm text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                  mode === "register" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="tab-register"
              >
                <UserPlus className="h-4 w-4" /> Inscription
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {mode === "login" ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login" className="text-sm font-medium">Login</Label>
                  <Input
                    id="login"
                    data-testid="login-input"
                    type="text"
                    placeholder="Votre login"
                    value={formData.login}
                    onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                    className="bg-input border-transparent focus:border-primary"
                    required
                    maxLength={100}
                    autoComplete="username"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Votre mot de passe"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-input border-transparent focus:border-primary pr-10"
                      required
                      maxLength={200}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="toggle-password"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Captcha */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Vérification</Label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-muted/50 border border-border rounded-sm p-3 font-mono text-lg text-center">
                      <span className="text-primary">{captcha.num1}</span>
                      <span className="text-muted-foreground mx-2">+</span>
                      <span className="text-accent">{captcha.num2}</span>
                      <span className="text-muted-foreground mx-2">=</span>
                      <span className="text-foreground">?</span>
                    </div>
                    <button
                      type="button"
                      onClick={fetchCaptcha}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors"
                      data-testid="refresh-captcha"
                    >
                      <RefreshCw className="h-5 w-5" />
                    </button>
                  </div>
                  <Input
                    id="captcha"
                    data-testid="captcha-input"
                    type="number"
                    placeholder="Réponse"
                    value={formData.captchaAnswer}
                    onChange={(e) => setFormData({ ...formData, captchaAnswer: e.target.value })}
                    className="bg-input border-transparent focus:border-primary"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="login-button"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                  disabled={loading}
                >
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Login</Label>
                  <Input
                    data-testid="register-login"
                    placeholder="Choisissez un login"
                    value={registerData.login}
                    onChange={(e) => setRegisterData({ ...registerData, login: e.target.value })}
                    className="bg-input border-transparent focus:border-primary"
                    required
                    maxLength={50}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Email</Label>
                  <Input
                    data-testid="register-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    className="bg-input border-transparent focus:border-primary"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Mot de passe</Label>
                  <Input
                    data-testid="register-password"
                    type="password"
                    placeholder="Minimum 6 caractères"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    className="bg-input border-transparent focus:border-primary"
                    required
                    minLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Confirmer le mot de passe</Label>
                  <Input
                    data-testid="register-confirm-password"
                    type="password"
                    placeholder="Confirmez votre mot de passe"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    className="bg-input border-transparent focus:border-primary"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Gift className="h-4 w-4 text-primary" /> Code Gift
                  </Label>
                  <Input
                    data-testid="register-gift-code"
                    placeholder="XXXX-XXXX-XXXX"
                    value={registerData.giftCode}
                    onChange={(e) => setRegisterData({ ...registerData, giftCode: e.target.value.toUpperCase() })}
                    className="bg-input border-transparent focus:border-primary font-mono"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Entrez le code fourni pour activer votre compte</p>
                </div>

                <Button
                  type="submit"
                  data-testid="register-button"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                  disabled={loading}
                >
                  {loading ? "Inscription..." : "S'inscrire"}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center space-y-1">
              <p className="text-xs text-muted-foreground">
                © 2026 - Tous droits réservés
              </p>
              <p className="text-[10px] text-muted-foreground/60 font-mono" data-testid="app-version">
                PanelHelper {APP_VERSION}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
